/**
 * What a plan's Age Pension change asks the client to DO (owner, 3 Oct 2026).
 *
 * A plan can make someone eligible who is not paid the pension today, or lift (or cut) a pension
 * they already get. None of it happens on its own: the pension is not paid automatically, and an
 * existing pension moves only once Services Australia knows about the changes. Every Statement of
 * Advice and the Action Plan say so from this one place, so they cannot drift apart.
 *
 * "Paid today" is read the way the Dashboard reads it: the do-nothing pension is above zero.
 */

export type PensionChangeKind = 'newly_eligible' | 'increase' | 'decrease' | 'none';

/** Under about $10 a fortnight either way is noise in a projection, not a change to act on. */
const MIN_CHANGE = 260;

export function pensionChangeKind(planPension: number, baselinePension: number): PensionChangeKind {
    const plan = Number.isFinite(planPension) ? planPension : 0;
    const base = Number.isFinite(baselinePension) ? baselinePension : 0;
    if (base < 1 && plan >= MIN_CHANGE) return 'newly_eligible';
    if (base >= 1 && plan - base >= MIN_CHANGE) return 'increase';
    if (base >= 1 && base - plan >= MIN_CHANGE) return 'decrease';
    return 'none';
}

const money = (v: number) => `$${Math.round(v).toLocaleString('en-AU')}`;

export interface PensionCallout {
    kind: Exclude<PensionChangeKind, 'none'>;
    /** For the implications page: a title, a one-line lead and the detail. Plain text. */
    implication: { title: string; lead: string; body: string };
    /** For the next steps list. Plain text. */
    nextStep: string;
}

/**
 * The call-out for a plan, or null when the pension does not move enough to act on.
 * `meansAssessment`: the plan also needs an aged care means assessment, so the order matters.
 */
export function pensionCallout(planPension: number, baselinePension: number, opts: { meansAssessment?: 'residential' | 'home' } = {}): PensionCallout | null {
    const kind = pensionChangeKind(planPension, baselinePension);
    if (kind === 'none') return null;
    const order = opts.meansAssessment === 'residential'
        ? ' Claim it before the aged care means assessment: once you are paid the pension, Services Australia already knows your income and assets, so the assessment needs only the home form (SA485), or no form at all if there is no home, instead of the full SA457.'
        : opts.meansAssessment === 'home'
            ? ' Claim it before the Support at Home means assessment: once you are paid the pension, Services Australia already knows your income and assets and no means assessment form is needed, instead of the SA456.'
            : '';
    if (kind === 'newly_eligible') {
        return {
            kind,
            implication: {
                title: 'Claiming the Age Pension',
                lead: `The plan qualifies you for about ${money(planPension)} a year of Age Pension. It is not paid automatically: you have to claim it.`,
                body: 'Claim once the changes in this plan are made, because Services Australia assesses what you own and earn on the day it decides your claim. Claim online through myGov (Centrelink), or on the Claim for Age Pension form (SA002) with the Income and assets form (SA369). You can lodge up to 13 weeks before you reach Age Pension age. The pension is generally paid from the day you claim, not before, so claim promptly once the changes are done.' + order,
            },
            nextStep: `Claim the Age Pension once the changes are made. It is not paid automatically, and the plan expects about ${money(planPension)} a year. Claim through myGov, or on the SA002 and SA369, which the Action Plan fills in for you.${opts.meansAssessment ? ' Do this before the means assessment.' : ''}`,
        };
    }
    if (kind === 'increase') {
        return {
            kind,
            implication: {
                title: 'Telling Services Australia',
                lead: `Your Age Pension is expected to rise from about ${money(baselinePension)} to ${money(planPension)} a year. It rises only once Services Australia knows about the changes.`,
                body: 'Report each change, such as a sale, a purchase or money moved, within 14 days, through myGov or by phone. The higher rate starts once they reassess you. Until then you are paid on what they last knew.',
            },
            nextStep: `Tell Services Australia about each change within 14 days, through myGov or by phone. Your pension is expected to rise from about ${money(baselinePension)} to ${money(planPension)} a year, and the higher rate starts only once they reassess you.`,
        };
    }
    return {
        kind,
        implication: {
            title: 'Telling Services Australia',
            lead: `Your Age Pension is expected to fall from about ${money(baselinePension)} to ${money(planPension)} a year under this plan.`,
            body: 'Report each change, such as a sale, a purchase or money moved, within 14 days, through myGov or by phone. Any pension paid at the old rate after a change you have not reported is a debt you have to repay.',
        },
        nextStep: `Tell Services Australia about each change within 14 days, through myGov or by phone. Your pension is expected to fall from about ${money(baselinePension)} to ${money(planPension)} a year, and anything overpaid after a change has to be repaid.`,
    };
}
